import React from 'react';
import PropTypes from 'prop-types';

import Button from '@decisiv/ui-components/lib/components/Button';
import Tabs, { Tab } from '@decisiv/ui-components/lib/components/Tabs';

import useEditUserForm from '../hooks/useEditUserForm';
import UserDetailsForm, { FORM_OPTIONS } from './UserDetailsForm';
import UserHeader from './UserHeader';
import { Actions, CancelLink, TabsArea } from './styles';

export const USER_DETAILS_TAB = 'user-details';

/**
 * Everything below the Management shell and the breadcrumb on
 * /management/users/:id/edit.
 *
 * All of the page's state lives here rather than in EditUser.jsx, and that split is
 * load-bearing: KeyDS LeftNav creates styled components inside a `useMemo`, so its
 * hook count changes between renders and re-rendering it throws "Rendered fewer hooks
 * than expected" on the React 16 portal pins. Typing in this form re-renders on every
 * keystroke, so the nav has to sit outside it — as do Breadcrumbs and the
 * NotificationsPanel. Same reason UsersContent exists.
 *
 * PageSurface belongs to EditUser rather than to this component, so that the
 * breadcrumb strip above shares the white surface with everything below it.
 */
function EditUserContent({ canEdit, user, usersPath }) {
  const {
    canSave,
    errors,
    isSaving,
    save,
    saved,
    setField,
    values,
  } = useEditUserForm(user);

  return (
    <>
      <UserHeader
        fullName={saved.fullName}
        lastLoginAt={saved.lastLoginAt}
        permissionAssignments={saved.permissionAssignments}
      />

      <TabsArea>
        <Tabs size="large" defaultSelectedId={USER_DETAILS_TAB}>
          <Tab id={USER_DETAILS_TAB} title="User Details">
            <UserDetailsForm
              disabled={!canEdit}
              errors={errors}
              onChange={setField}
              options={user.options}
              username={saved.username}
              values={values}
            />

            {canEdit && (
              <Actions>
                <Button
                  disabled={!canSave}
                  loading={isSaving}
                  loadingText="Saving..."
                  onClick={save}
                  text="Save Changes"
                />
                <CancelLink href={usersPath}>Cancel</CancelLink>
              </Actions>
            )}
          </Tab>

          {/* Managing account associations and permissions are both out of scope for
              PD-4068, so the tab the design shows is present but inert — the same
              treatment PD-4063 gives its CREATE USER options. */}
          <Tab disabled id="access-permissions" title="Access & Permissions" />
        </Tabs>
      </TabsArea>
    </>
  );
}

EditUserContent.propTypes = {
  /** False for a viewer holding user-management:Read but not :Modify. */
  canEdit: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    options: FORM_OPTIONS.isRequired,
  }).isRequired,
  usersPath: PropTypes.string.isRequired,
};

EditUserContent.defaultProps = {
  canEdit: false,
};

export default EditUserContent;
